'use client';

import { HTMLInputTypeAttribute, useContext, useState } from 'react';
import { FieldError, get, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { PropsWithClasses } from '@lamesarv-sdk/types';

import { CommonFormContext } from '.';

interface ICommonInputProps extends PropsWithClasses {
  name: string;
  type?: HTMLInputTypeAttribute;
  label?: string;
  hideLabel?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  customOptions?: { [key: string]: string | boolean | number };
  classContainer?: string;
  classLabel?: string;
  classLabelWithValue?: string;
  classInputWithValue?: string;
}

export const CommonInput = (props: ICommonInputProps) => {
  const [value, setValue] = useState<string>();

  const {
    register,
    trigger,
    clearErrors,
    setValue: setFormValue,
    formState: { errors },
  } = useFormContext();
  const { isDisabled } = useContext(CommonFormContext);

  const error = get(errors, props.name) as FieldError | null;

  return (
    <div className={twMerge('flex flex-col gap-1.5 group', isDisabled && 'opacity-60', props.classContainer)}>
      {!props.hideLabel && (
        <span className={twMerge('text-sm text-black-500', props.classLabel, !!value && props.classLabelWithValue)}>
          {props.label}
          {props.isRequired && <span className="ml-1 text-red-700 leading-none">*</span>}
        </span>
      )}
      <input
        {...register(props.name)}
        type={props.type}
        className={twMerge(
          'border border-black-200 rounded-sm px-3 py-2 placeholder-black-350 text-black-500 w-full',
          props.classInput,
          !!value && props.classInputWithValue,
          error && 'border-red-700 placeholder-red-400 text-red-700',
        )}
        placeholder={props.placeholder}
        required={props.isRequired}
        disabled={isDisabled}
        onKeyDown={async (event) => {
          if (event.key === 'Enter') {
            const currentDOMValue = (event.target as HTMLInputElement).value;

            setFormValue(props.name, currentDOMValue, {
              shouldValidate: false,
              shouldDirty: true,
              shouldTouch: true,
            });

            // Small delay to ensure the value is updated in React Hook Form's
            // state, then trigger validation with the updated value
            setTimeout(() => {
              clearErrors(props.name);
              trigger(props.name);
            }, 10);
          }
        }}
        onChange={(event) => {
          const currentDOMValue = (event.target as HTMLInputElement).value;

          setValue(currentDOMValue);
          setFormValue(props.name, currentDOMValue, {
            shouldValidate: false,
            shouldDirty: true,
            shouldTouch: true,
          });
        }}
        onBlur={() => {
          trigger(props.name);
        }}
        {...props.customOptions}
      />
      {error && <span className="text-left text-red-700 text-sm">{error.message}</span>}
    </div>
  );
};
